import { Button } from "components/DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useDeleteFromRecycleBinMutation,
  useRestoreFromRecycleBinMutation,
} from "store/apis/documents";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { OptionDropdown } from "components/icons/optionDropdown";
import { DeleteConfirmationModal } from "../DeleteConfirmationModal";
import { stopPropagation } from "utils/stopPropagation";
import { RestoreConfirmation } from "./RestoreConfirmation";

type RecycleBinActionsProps = {
  fileId: string;
  groupId: string;
  selectedItemLength: number;
};

export const RecycleBinActions = ({
  fileId,
  groupId,
  selectedItemLength,
}: RecycleBinActionsProps) => {
  const { alertToast, successToast } = useToast();
  const deleteModal = useModal();
  const restoreModal = useModal();

  const [restoreFromRecycleBin, { isLoading: isRestoring }] =
    useRestoreFromRecycleBinMutation();
  const [deleteFromRecycleBin, { isLoading: isDeleting }] =
    useDeleteFromRecycleBinMutation();

  const handleRestore = async () => {
    try {
      await restoreFromRecycleBin({ groupId, files: [fileId] }).unwrap();
      successToast({
        title: "Restored successfully",
        message: "The file has been moved back to its original location.",
      });
      restoreModal.close();
    } catch (error: any) {
      alertToast({
        message: error?.data?.error?.message || "Failed to restore file",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFromRecycleBin({ groupId, files: [fileId] }).unwrap();
      successToast({
        title: "Deleted successfully",
        message: "The file has been permanently deleted",
      });
      deleteModal.close();
    } catch (error: any) {
      alertToast({
        message: error?.data?.error?.message || "Failed to delete file",
      });
    }
  };

  return (
    <span onClick={stopPropagation}>
      <DropDown.Root>
        <DropDown.Trigger asChild>
          <div className="t-size-6 t-rounded hover:t-bg-purple-20 data-state-open:t-bg-purple-20 t-items-center t-justify-center t-flex t-text-text-30">
            <OptionDropdown />
          </div>
        </DropDown.Trigger>
        <DropDown.Content align="end">
          <DropDown.Item onSelect={restoreModal.open} disabled={isRestoring}>
            Restore
          </DropDown.Item>
          <DropDown.Item onSelect={deleteModal.open} type="danger">
            Delete permanently
          </DropDown.Item>
        </DropDown.Content>
      </DropDown.Root>
      <DeleteConfirmationModal
        {...deleteModal}
        isLoading={isDeleting}
        confirm={handleDelete}
        type="SINGLE"
      />
      <RestoreConfirmation
        {...restoreModal}
        isLoading={isRestoring}
        confirm={handleRestore}
      />
    </span>
  );
};
